import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppShell } from "../../components/Sidebar";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import {
  Plus, Trash2, X, Video, Link as LinkIcon, Upload, ChevronLeft,
  Pencil, Save, Globe2, FileVideo, FileText, Image as ImageIcon, Presentation,
  ExternalLink, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const MATERIAL_TYPES = [
  { value: "video",        label: "Video",        icon: FileVideo,    accept: "video/*,.mp4,.mov,.webm,.mkv,.m4v,.avi", hint: ".mp4 / .mov / .webm / .mkv / .m4v / .avi" },
  { value: "pdf",          label: "PDF",          icon: FileText,     accept: ".pdf,application/pdf",                  hint: ".pdf" },
  { value: "image",        label: "Image",        icon: ImageIcon,    accept: "image/*,.jpg,.jpeg,.png,.gif,.webp,.svg", hint: ".jpg / .jpeg / .png / .gif / .webp / .svg" },
  { value: "presentation", label: "Presentation", icon: Presentation, accept: ".pdf,.ppt,.pptx,.key,.odp",            hint: ".pdf / .ppt / .pptx / .key / .odp" },
  { value: "link",         label: "Link",         icon: LinkIcon,     accept: "",                                       hint: "Any web URL — webpage, article, online tool, etc.", urlOnly: true },
];

const typeMeta = (t) => MATERIAL_TYPES.find((x) => x.value === t) || MATERIAL_TYPES[0];

const inp = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";

export default function ModuleSubmodules({ role = "admin" }) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [submodules, setSubmodules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const lang = i18n.resolvedLanguage || "en";
      const [mods, subs] = await Promise.all([
        api.get(`/admin/modules?lang=${lang}`),
        api.get(`/admin/modules/${moduleId}/submodules?lang=${lang}`),
      ]);
      setModule(mods.data.find((m) => m.id === moduleId) || null);
      setSubmodules(subs.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleId, i18n.resolvedLanguage]);

  const onSubmoduleCreated = (s) => setSubmodules((prev) => [...prev, s]);
  const onSubmoduleUpdated = (s) =>
    setSubmodules((prev) => prev.map((x) => (x.id === s.id ? s : x)));
  const onSubmoduleDeleted = (id) =>
    setSubmodules((prev) => prev.filter((x) => x.id !== id));

  const backPath = role === "teacher" ? "/teacher/manage/modules" : "/admin/modules";

  return (
    <AppShell role={role}>
      <button
        onClick={() => navigate(backPath)}
        data-testid="back-to-modules"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
      >
        <ChevronLeft size={14} /> Back to Chapters
      </button>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" data-testid="module-title">
            {module?.display_name || module?.name || "Chapter"}
          </h1>
          <p className="mt-1 text-slate-500">
            Add sub-chapters, learning outcomes and learning materials (videos, PDFs, images, presentations or links) in any language.
            Changes reflect instantly in teacher and student portals.
          </p>
        </div>
        <button
          onClick={() => setAdding(true)}
          data-testid="add-submodule-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700"
        >
          <Plus size={16} /> Add Sub-chapter
        </button>
      </div>

      {/* Module learning outcomes summary */}
      {module && (module.outcomes?.length > 0 || module.objectives?.length > 0) && (
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          {module.objectives?.length > 0 && (
            <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
              <h3 className="text-sm font-bold text-slate-900">Chapter Objectives</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
                {(module.display_objectives || module.objectives).map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}
          {module.outcomes?.length > 0 && (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-5 shadow-card">
              <h3 className="text-sm font-bold text-emerald-900">Module Learning Outcomes</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-emerald-800">
                {(module.display_outcomes || module.outcomes).map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <p className="mt-10 text-slate-500">Loading…</p>
      ) : submodules.length === 0 ? (
        <div className="mt-8 rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center" data-testid="empty-submodules">
          <Video size={32} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm font-semibold text-slate-700">No sub-chapters yet</p>
          <p className="mt-1 text-xs text-slate-500">Click "Add Sub-chapter" to create your first sub-chapter and attach videos in any language.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {submodules.map((s, i) => (
            <SubmoduleCard
              key={s.id}
              index={i + 1}
              data={s}
              role={role}
              userId={user?.id}
              onUpdated={onSubmoduleUpdated}
              onRefresh={load}
              onDelete={() => setConfirmDel(s)}
            />
          ))}
        </div>
      )}

      {adding && (
        <SubmoduleEditModal
          moduleId={moduleId}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); load(); toast.success("Sub-chapter created"); }}
        />
      )}

      {confirmDel && (
        <ConfirmDelete
          title={`Delete "${confirmDel.display_name || confirmDel.name}"?`}
          body={`This will remove the sub-chapter and ${confirmDel.videos?.length || 0} video(s). This cannot be undone.`}
          onCancel={() => setConfirmDel(null)}
          onConfirm={async () => {
            await api.delete(`/admin/submodules/${confirmDel.id}`);
            onSubmoduleDeleted(confirmDel.id);
            setConfirmDel(null);
            toast.success("Sub-chapter deleted");
          }}
        />
      )}
    </AppShell>
  );
}

const SubmoduleCard = ({ index, data, role = "admin", userId, onUpdated, onRefresh, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [videoModal, setVideoModal] = useState(false);
  const [bulkModal, setBulkModal] = useState(false);
  const [confirmVidDel, setConfirmVidDel] = useState(null);
  const canDeleteSub = role === "admin" || data.created_by === userId;
  const canDeleteVid = (v) => role === "admin" || v.created_by === userId;

  const removeVideo = async (vid) => {
    await api.delete(`/admin/videos/${vid.id}`);
    onUpdated({ ...data, videos: data.videos.filter((v) => v.id !== vid.id) });
    setConfirmVidDel(null);
    toast.success("Video removed");
  };

  const onVideoAdded = (v) =>
    onUpdated({ ...data, videos: [...(data.videos || []), v] });

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card" data-testid={`submodule-card-${data.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-sm font-bold text-indigo-700">{index}</div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">{data.display_name || data.name}</h3>
            {(data.display_outcomes?.length || data.outcomes?.length) > 0 && (
              <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs text-slate-600">
                {(data.display_outcomes || data.outcomes).map((o, k) => <li key={k}>{o}</li>)}
              </ul>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setEditing(true)}
            data-testid={`edit-submodule-${data.id}`}
            className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            <Pencil size={12} /> Edit
          </button>
          {canDeleteSub && (
            <button
              onClick={onDelete}
              data-testid={`delete-submodule-${data.id}`}
              className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100"
            >
              <Trash2 size={12} /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-700">Materials ({data.videos?.length || 0})</h4>
          <div className="flex gap-2">
            {role === "admin" && (
              <button
                onClick={() => setBulkModal(true)}
                data-testid={`bulk-upload-${data.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100"
              >
                <Upload size={12} /> Bulk Upload
              </button>
            )}
            <button
              onClick={() => setVideoModal(true)}
              data-testid={`add-video-${data.id}`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-violet-700"
            >
              <Plus size={12} /> Add Material
            </button>
          </div>
        </div>
        {data.videos?.length > 0 ? (
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {data.videos.map((v) => {
              const meta = typeMeta(v.material_type || "video");
              const Icon = meta.icon;
              return (
                <div key={v.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid={`video-${v.id}`}>
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white text-violet-600">
                      <Icon size={16} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700">
                          <Globe2 size={10} /> {v.language}
                        </span>
                        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                          {meta.label} · {v.kind}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-xs font-medium text-slate-700">{v.title || v.original_filename || v.url}</p>
                    </div>
                  </div>
                  {canDeleteVid(v) && (
                    <button
                      onClick={() => setConfirmVidDel(v)}
                      data-testid={`delete-video-${v.id}`}
                      className="flex-none text-slate-400 hover:text-rose-600"
                      aria-label="Remove material"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mt-3 rounded-xl bg-slate-50 px-3 py-3 text-xs text-slate-500">No materials yet — click "Add Material" to upload a video, PDF, image, presentation or paste a link in any language.</p>
        )}
      </div>

      {editing && (
        <SubmoduleEditModal
          moduleId={data.module_id}
          existing={data}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onRefresh?.(); toast.success("Sub-chapter saved"); }}
        />
      )}
      {videoModal && (
        <VideoAddModal
          submoduleId={data.id}
          onClose={() => setVideoModal(false)}
          onAdded={(v) => { setVideoModal(false); onVideoAdded(v); toast.success("Video added"); }}
        />
      )}
      {bulkModal && (
        <BulkUploadModal
          submoduleId={data.id}
          submoduleName={data.display_name || data.name}
          onClose={(uploadedAny) => { setBulkModal(false); if (uploadedAny) onRefresh?.(); }}
        />
      )}
      {confirmVidDel && (
        <ConfirmDelete
          title="Remove this video?"
          body={`The "${confirmVidDel.language}" video will be removed permanently.`}
          onCancel={() => setConfirmVidDel(null)}
          onConfirm={() => removeVideo(confirmVidDel)}
        />
      )}
    </div>
  );
};

const SubmoduleEditModal = ({ moduleId, existing, onClose, onSaved }) => {
  const [name, setName] = useState(existing?.name || "");
  const [outcomes, setOutcomes] = useState(existing?.outcomes?.length ? existing.outcomes : [""]);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: name.trim(), outcomes: outcomes.map((s) => s.trim()).filter(Boolean) };
      let res;
      if (existing) {
        res = await api.put(`/admin/submodules/${existing.id}`, payload);
      } else {
        res = await api.post(`/admin/modules/${moduleId}/submodules`, payload);
      }
      onSaved(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save submodule");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm">
      <form onSubmit={submit} className="my-8 w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl" data-testid="submodule-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-slate-900">{existing ? "Edit Sub-chapter" : "Add Sub-chapter"}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700">Sub-chapter Name</span>
            <input
              required
              data-testid="submodule-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Properties of Sound Waves"
              className={inp}
            />
          </label>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Learning Outcomes</span>
              <button type="button" onClick={() => setOutcomes((o) => [...o, ""])} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">+ Add outcome</button>
            </div>
            <div className="space-y-2">
              {outcomes.map((o, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    data-testid={`submodule-outcome-${i}`}
                    value={o}
                    onChange={(e) => setOutcomes((arr) => arr.map((x, k) => (k === i ? e.target.value : x)))}
                    placeholder="e.g. Calculate the frequency of a wave"
                    className={`${inp} flex-1`}
                  />
                  <button
                    type="button"
                    onClick={() => setOutcomes((arr) => arr.filter((_, k) => k !== i).length ? arr.filter((_, k) => k !== i) : [""])}
                    className="rounded-xl border border-slate-200 bg-white px-3 text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="submodule-save" disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60">
            <Save size={14} /> {saving ? "Saving…" : existing ? "Save Changes" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
};

const VideoAddModal = ({ submoduleId, onClose, onAdded }) => {
  const [matType, setMatType] = useState("video");
  const meta = typeMeta(matType);
  const [tab, setTab] = useState(meta.urlOnly ? "url" : "url"); // url | file
  const [language, setLanguage] = useState("English");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // If user picks a Link material, force URL tab
  const onTypeChange = (v) => {
    setMatType(v);
    if (typeMeta(v).urlOnly) setTab("url");
    setFile(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!language.trim()) { toast.error("Please enter a language"); return; }
    setSubmitting(true);
    try {
      let res;
      if (tab === "url") {
        if (!url.trim()) { toast.error("Please paste a URL"); setSubmitting(false); return; }
        res = await api.post(`/admin/submodules/${submoduleId}/videos/url`, {
          language: language.trim(), url: url.trim(), title: title.trim(),
          material_type: matType,
        });
      } else {
        if (!file) { toast.error("Please choose a file"); setSubmitting(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        const params = new URLSearchParams({
          language: language.trim(), title: title.trim(), material_type: matType,
        });
        res = await api.post(`/admin/submodules/${submoduleId}/videos/upload?${params}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
        });
      }
      onAdded(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add material");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm">
      <form onSubmit={submit} className="my-8 w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl" data-testid="video-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-slate-900">Add Material</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        {/* Material type picker */}
        <div className="mt-4">
          <span className="mb-2 block text-sm font-medium text-slate-700">Material Type</span>
          <div className="grid grid-cols-5 gap-2">
            {MATERIAL_TYPES.map((m) => {
              const Icon = m.icon;
              const isActive = matType === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => onTypeChange(m.value)}
                  data-testid={`material-type-${m.value}`}
                  className={`flex flex-col items-center gap-1 rounded-xl border-2 p-2.5 text-xs font-semibold transition ${
                    isActive
                      ? "border-violet-500 bg-violet-50 text-violet-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-violet-200 hover:bg-violet-50/40"
                  }`}
                >
                  <Icon size={18} />
                  {m.label}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-slate-500">{meta.hint}</p>
        </div>

        {/* URL / File tab toggle (hidden for Link type — URL only) */}
        {!meta.urlOnly && (
          <div className="mt-4 inline-flex rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setTab("url")}
              data-testid="video-tab-url"
              className={`rounded-lg px-4 py-1.5 text-xs font-semibold transition ${tab === "url" ? "bg-white text-slate-900 shadow" : "text-slate-600"}`}
            >
              <LinkIcon size={12} className="mr-1 inline" /> Paste URL
            </button>
            <button
              type="button"
              onClick={() => setTab("file")}
              data-testid="video-tab-file"
              className={`rounded-lg px-4 py-1.5 text-xs font-semibold transition ${tab === "file" ? "bg-white text-slate-900 shadow" : "text-slate-600"}`}
            >
              <Upload size={12} className="mr-1 inline" /> Upload File
            </button>
          </div>
        )}

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700">Language</span>
            <input
              required
              list="lang-suggestions"
              data-testid="video-language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              placeholder="Type or pick (e.g. English, Hindi, Assamese, Bengali, Tamil)"
              className={inp}
            />
            <datalist id="lang-suggestions">
              {["English", "Hindi", "Assamese", "Bengali", "Tamil", "Telugu", "Marathi", "Gujarati", "Kannada", "Malayalam", "Punjabi", "Odia", "Urdu"].map((l) => (
                <option key={l} value={l} />
              ))}
            </datalist>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700">Title (optional)</span>
            <input
              data-testid="video-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={matType === "link" ? "e.g. Khan Academy lesson on Sound" : `e.g. Introduction to ${meta.label}`}
              className={inp}
            />
          </label>

          {tab === "url" ? (
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">{meta.label} URL</span>
              <input
                data-testid="video-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={
                  matType === "video"
                    ? "https://www.youtube.com/watch?v=... or any direct .mp4 link"
                    : matType === "pdf"
                    ? "https://example.com/notes.pdf"
                    : matType === "image"
                    ? "https://example.com/diagram.png"
                    : matType === "presentation"
                    ? "https://docs.google.com/presentation/d/..."
                    : "https://example.com/article"
                }
                className={inp}
              />
              {matType === "video" && (
                <span className="mt-1 block text-xs text-slate-500">YouTube, Vimeo or direct mp4/webm links work.</span>
              )}
            </label>
          ) : (
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">{meta.label} File ({meta.hint})</span>
              <input
                data-testid="video-file"
                type="file"
                accept={meta.accept}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-700 file:mr-4 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-indigo-700"
              />
              {file && (
                <span className="mt-2 block text-xs text-slate-500">
                  {file.name} • {(file.size / (1024 * 1024)).toFixed(1)} MB
                </span>
              )}
              {progress > 0 && progress < 100 && (
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full bg-indigo-600 transition-all" style={{ width: `${progress}%` }} />
                </div>
              )}
            </label>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="video-save" disabled={submitting} className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-violet-700 disabled:opacity-60">
            <Save size={14} /> {submitting ? "Adding…" : "Add Material"}
          </button>
        </div>
      </form>
    </div>
  );
};

const EXT_TO_TYPE = {
  mp4: "video", mov: "video", webm: "video", mkv: "video", m4v: "video", avi: "video",
  pdf: "pdf",
  jpg: "image", jpeg: "image", png: "image", gif: "image", webp: "image", svg: "image",
  ppt: "presentation", pptx: "presentation", key: "presentation", odp: "presentation",
};

const detectType = (name) => EXT_TO_TYPE[(name.split(".").pop() || "").toLowerCase()] || null;
const cleanTitle = (name) => name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();

const BulkUploadModal = ({ submoduleId, submoduleName, onClose }) => {
  const [language, setLanguage] = useState("English");
  const [items, setItems] = useState([]); // {file, type, status: queued|uploading|done|error, progress, error}
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const doneCount = items.filter((x) => x.status === "done").length;
  const allFinished = items.length > 0 && items.every((x) => x.status === "done" || x.status === "error");

  const addFiles = (fileList) => {
    const next = [];
    for (const f of Array.from(fileList || [])) {
      const type = detectType(f.name);
      if (!type) { toast.error(`"${f.name}" skipped — unsupported file type`); continue; }
      next.push({ file: f, type, status: "queued", progress: 0, error: null });
    }
    if (next.length) setItems((prev) => [...prev, ...next]);
  };

  const startUpload = async () => {
    if (!language.trim()) { toast.error("Please enter a language"); return; }
    setUploading(true);
    for (let i = 0; i < items.length; i++) {
      if (items[i].status === "done") continue;
      setItems((prev) => prev.map((x, k) => (k === i ? { ...x, status: "uploading", progress: 0, error: null } : x)));
      try {
        const fd = new FormData();
        fd.append("file", items[i].file);
        const params = new URLSearchParams({
          language: language.trim(), title: cleanTitle(items[i].file.name), material_type: items[i].type,
        });
        await api.post(`/admin/submodules/${submoduleId}/videos/upload?${params}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) {
              const p = Math.round((e.loaded / e.total) * 100);
              setItems((prev) => prev.map((x, k) => (k === i ? { ...x, progress: p } : x)));
            }
          },
        });
        setItems((prev) => prev.map((x, k) => (k === i ? { ...x, status: "done", progress: 100 } : x)));
      } catch (err) {
        const msg = err?.response?.data?.detail || "Upload failed";
        setItems((prev) => prev.map((x, k) => (k === i ? { ...x, status: "error", error: msg } : x)));
      }
    }
    setUploading(false);
  };

  const removeItem = (i) => setItems((prev) => prev.filter((_, k) => k !== i));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" data-testid="bulk-upload-modal">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Bulk Upload Materials</h3>
            <p className="mt-0.5 text-xs text-slate-500">Sub-chapter: {submoduleName}</p>
          </div>
          <button type="button" onClick={() => onClose(doneCount > 0)} className="text-slate-400 hover:text-slate-600" data-testid="bulk-upload-close">
            <X size={20} />
          </button>
        </div>

        <label className="mt-4 block">
          <span className="mb-1.5 block text-sm font-medium text-slate-700">Language (applies to all files)</span>
          <input
            required
            list="lang-suggestions"
            data-testid="bulk-upload-language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={uploading}
            className={inp}
          />
        </label>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!uploading) addFiles(e.dataTransfer.files); }}
          data-testid="bulk-upload-dropzone"
          className={`mt-4 rounded-2xl border-2 border-dashed p-6 text-center transition ${
            dragOver ? "border-violet-500 bg-violet-50" : "border-slate-200 bg-slate-50/60"
          }`}
        >
          <Upload size={26} className="mx-auto text-violet-500" />
          <p className="mt-2 text-sm font-semibold text-slate-700">Drag &amp; drop files here</p>
          <p className="mt-0.5 text-xs text-slate-500">Videos (.mp4 .mov .webm…), PDFs, images (.jpg .png…), presentations (.ppt .pptx). Type is detected automatically.</p>
          <label className="mt-3 inline-block cursor-pointer rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700">
            Choose Files
            <input
              type="file"
              multiple
              data-testid="bulk-upload-input"
              disabled={uploading}
              accept=".mp4,.mov,.webm,.mkv,.m4v,.avi,.pdf,.jpg,.jpeg,.png,.gif,.webp,.svg,.ppt,.pptx,.key,.odp"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              className="hidden"
            />
          </label>
        </div>

        {items.length > 0 && (
          <div className="mt-4 max-h-64 space-y-2 overflow-y-auto pr-1" data-testid="bulk-upload-file-list">
            {items.map((it, i) => {
              const meta = typeMeta(it.type);
              const Icon = meta.icon;
              return (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-2.5" data-testid={`bulk-file-${i}`}>
                  <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white text-violet-600"><Icon size={16} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-xs font-semibold text-slate-800">{it.file.name}</p>
                      <span className="flex-none rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase text-violet-700">{meta.label}</span>
                      <span className="flex-none text-[10px] text-slate-400">{(it.file.size / (1024 * 1024)).toFixed(1)} MB</span>
                    </div>
                    {it.status === "uploading" && (
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200">
                        <div className="h-full bg-indigo-600 transition-all" style={{ width: `${it.progress}%` }} />
                      </div>
                    )}
                    {it.status === "error" && <p className="mt-0.5 text-[11px] font-medium text-rose-600">{it.error}</p>}
                  </div>
                  <div className="flex-none">
                    {it.status === "queued" && !uploading && (
                      <button onClick={() => removeItem(i)} className="text-slate-400 hover:text-rose-600" data-testid={`bulk-file-remove-${i}`}><X size={15} /></button>
                    )}
                    {it.status === "uploading" && <span className="text-[11px] font-semibold text-indigo-600">{it.progress}%</span>}
                    {it.status === "done" && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Done</span>}
                    {it.status === "error" && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">Failed</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500" data-testid="bulk-upload-status">
            {items.length > 0 && `${doneCount}/${items.length} uploaded`}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onClose(doneCount > 0)}
              disabled={uploading}
              className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              {allFinished ? "Close" : "Cancel"}
            </button>
            {!allFinished && (
              <button
                type="button"
                onClick={startUpload}
                disabled={uploading || items.length === 0}
                data-testid="bulk-upload-start"
                className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-violet-700 disabled:opacity-60"
              >
                <Upload size={14} /> {uploading ? "Uploading…" : `Upload ${items.length || ""} File${items.length === 1 ? "" : "s"}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfirmDelete = ({ title, body, onCancel, onConfirm }) => (  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="confirm-delete-modal">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 text-rose-600"><AlertTriangle size={20} /></div>
        <h3 className="text-xl font-bold text-slate-900">{title}</h3>
      </div>
      <p className="mt-3 text-sm text-slate-600">{body}</p>
      <div className="mt-5 flex justify-end gap-3">
        <button onClick={onCancel} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
        <button onClick={onConfirm} data-testid="confirm-delete-confirm" className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-rose-700">Yes, Delete</button>
      </div>
    </div>
  </div>
);
